import { ActivityAllocation, ProjectAsset, ResearchActivity } from '@avanzu/types';
import { DeleteSweep, PlaylistAddCheck } from '@mui/icons-material';
import { IconButton, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { memo, useCallback, useEffect, useState } from 'react';
import formatCurrency from '../../../utils/formatCurrency';
import CustomPercentageInput from './CustomPercentageInput';

// Asset table row component
interface AssetTableRowProps {
  asset: ProjectAsset;
  activities: ResearchActivity[];
  allocations: Record<string, number>;
  projectAllocation: number;
  onCreateRule: (
    assetId: string,
    projectAllocation: number,
    allocations: ActivityAllocation[]
  ) => void;
  onDeleteRule: (assetId: string) => void;
  isSaving: boolean;
}

export const AssetTableRow = memo<AssetTableRowProps>(
  ({ asset, activities, allocations, projectAllocation, onCreateRule, onDeleteRule, isSaving }) => {
    const [localAllocations, setLocalAllocations] = useState(allocations);
    const [localProjectAllocation, setLocalProjectAllocation] = useState(projectAllocation);

    useEffect(() => setLocalAllocations(allocations), [allocations]);
    useEffect(() => setLocalProjectAllocation(projectAllocation), [projectAllocation]);

    const totalAllocated = Object.values(localAllocations).reduce((sum, val) => sum + val, 0);
    const isComplete = localProjectAllocation === 0 || totalAllocated === 100;
    const canAllocateActivities = localProjectAllocation > 0;
    const hasExistingAllocation =
      (asset.allocations && asset.allocations.length > 0) ||
      (asset.projectAllocation && asset.projectAllocation > 0);

    const handleActivityChange = useCallback((activityId: string, value: number) => {
      setLocalAllocations((prev) => ({
        ...prev,
        [activityId]: value,
      }));
    }, []);

    const handleProjectAllocationChange = useCallback((value: number) => {
      setLocalProjectAllocation(value);
      if (value === 0) setLocalAllocations({});
    }, []);

    const handleCreateRule = () => {
      const allocationsList: ActivityAllocation[] = Object.entries(localAllocations)
        .filter(([_, percentage]) => percentage > 0)
        .map(([activityId, allocation]) => ({ activityId, allocation }));
      onCreateRule(asset.id, localProjectAllocation, allocationsList);
    };

    const handleDeleteRule = () => onDeleteRule(asset.id);

    const isAllocateDisabled = !isComplete || isSaving || localProjectAllocation === 0;
    const isDeleteDisabled = !hasExistingAllocation || isSaving;

    return (
      <TableRow sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
        {/* Asset Details */}
        <TableCell sx={{ width: 350, minWidth: 350 }}>
          <Stack>
            <Typography variant="body2" fontWeight={600}>
              {asset.assetName}
            </Typography>
            {asset.assetNumber && (
              <Typography variant="caption" color="text.secondary">
                {asset.assetNumber} • Cost: {formatCurrency(asset.purchasePrice)}
              </Typography>
            )}
          </Stack>
        </TableCell>

        {/* Action Buttons */}
        <TableCell sx={{ width: 120, textAlign: 'center' }}>
          <Stack direction="row" justifyContent="center" spacing={0}>
            <Tooltip title="Remove asset allocation" arrow>
              <span>
                <IconButton
                  color="error"
                  disabled={isDeleteDisabled}
                  size="small"
                  onClick={handleDeleteRule}
                >
                  <DeleteSweep fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title={hasExistingAllocation ? 'Update allocation' : 'Create allocation'}
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  color={hasExistingAllocation ? 'success' : 'primary'}
                  disabled={isAllocateDisabled}
                  onClick={handleCreateRule}
                >
                  <PlaylistAddCheck fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </TableCell>

        {/* Project Allocation */}
        <TableCell sx={{ backgroundColor: '#EFF2FE', textAlign: 'center' }}>
          <CustomPercentageInput
            value={localProjectAllocation}
            onChange={handleProjectAllocationChange}
            disabled={isSaving}
            fontWeight={600}
            width={80}
          />
        </TableCell>

        {/* Total */}
        <TableCell sx={{ textAlign: 'center' }}>
          <Typography
            variant="caption"
            color={
              !canAllocateActivities
                ? 'text.disabled'
                : isComplete
                  ? 'success.main'
                  : totalAllocated > 100
                    ? 'error'
                    : 'text.secondary'
            }
          >
            {canAllocateActivities ? `${totalAllocated.toFixed(0)}%` : '-'}
          </Typography>
        </TableCell>

        {/* Activity Allocations */}
        {activities.map((activity) => (
          <TableCell key={activity.id} sx={{ textAlign: 'center', p: 1 }}>
            <CustomPercentageInput
              value={localAllocations[activity.id] || 0}
              onChange={(value) => handleActivityChange(activity.id, value)}
              disabled={!canAllocateActivities || isSaving}
              width={80}
            />
          </TableCell>
        ))}
      </TableRow>
    );
  },
  (prev, next) =>
    prev.asset.id === next.asset.id &&
    prev.projectAllocation === next.projectAllocation &&
    prev.isSaving === next.isSaving &&
    JSON.stringify(prev.allocations) === JSON.stringify(next.allocations) &&
    prev.activities.length === next.activities.length
);

AssetTableRow.displayName = 'AssetTableRow';

export default AssetTableRow;
